import React from "react";
import { X, ArrowUpDown, Search } from "lucide-react";
import { CreatedFilter, createdRange, inCreatedRange } from "../crm/CreatedFilter";
import { ScopeSelect, REGION_LABELS } from "../ScopeSelect";

export const CO_GROUPS = [
  { id: "approval", zh: "内部审批中", en: "Internal approval", statuses: ["Draft", "Engineering Review", "Revision Requested", "Engineering Approved", "Finance Review", "Finance Approved"] },
  { id: "customer", zh: "待客户签字 / 付款", en: "Customer sign / pay", statuses: ["Sent to Customer", "Customer Signed", "Invoice Created", "Awaiting Payment", "Partially Paid"] },
  { id: "released", zh: "已放行施工", en: "Released", statuses: ["Released for Construction", "Scheduled", "In Progress"] },
  { id: "done", zh: "已完成 / 关闭", en: "Done / closed", statuses: ["Completed", "Customer Verified", "Closed"] },
  { id: "rejected", zh: "已拒绝 / 取消", en: "Rejected / cancelled", statuses: ["Rejected", "Cancelled"] },
];
export const CO_UNLOCKED = ["Released for Construction", "Scheduled", "In Progress", "Completed", "Customer Verified", "Closed"];
export const CO_FILTER_KEYS = ["q", "group", "region", "lock", "sales", "created", "cfrom", "cto", "sort"];
const SORTS = [
  { id: "", zh: "排序：创建 新→旧", en: "Sort: newest" },
  { id: "created_asc", zh: "创建 旧→新", en: "Oldest first" },
  { id: "amount_desc", zh: "金额 高→低", en: "Amount high→low" },
  { id: "number_asc", zh: "CO # 升序", en: "CO # ascending" },
];
const sel = "text-sm rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 focus:border-indigo-400 focus:outline-none";
const on = "border-indigo-400 text-indigo-800 bg-indigo-50/60";
const amt = (co) => Number(co.approvedPrice || co.estimatedPrice || 0);
const isUnlocked = (co) => CO_UNLOCKED.includes(co.status) || !!co.lockOverride;

/* 按 URL 参数在前端筛选 + 排序 Change Order 列表（接口一次返回全部） */
export const applyCoFilters = (list, params) => {
  const g = (k) => params.get(k) || "";
  const q = g("q").trim().toLowerCase();
  const grp = CO_GROUPS.find((x) => x.id === g("group"));
  const range = createdRange({ mode: g("created"), from: g("cfrom"), to: g("cto") });
  let rows = list.filter((co) =>
    (!q || [co.number, co.address, co.clientName, co.newScope, co.salesperson, co.projectId].some((v) => (v || "").toLowerCase().includes(q)))
    && (!grp || grp.statuses.includes(co.status))
    && (!g("region") || (co.region || "") === g("region"))
    && (!g("lock") || (g("lock") === "unlocked") === isUnlocked(co))
    && (!g("sales") || (co.salesperson || "") === g("sales"))
    && inCreatedRange(co, range));
  const s = g("sort");
  if (s === "created_asc") rows = [...rows].sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || ""));
  else if (s === "amount_desc") rows = [...rows].sort((a, b) => amt(b) - amt(a));
  else if (s === "number_asc") rows = [...rows].sort((a, b) => (a.number || "").localeCompare(b.number || ""));
  return rows;
};

/* 变更订单 · 快速筛选栏（与销售 CRM 同款）：搜索 / 阶段分组 / 分公司 / 施工锁 / 销售 / 创建时间 / 排序 */
export const CoQuickFilters = ({ zh, params, setParams, list, count }) => {
  const get = (k) => params.get(k) || "";
  const set = (patch) => { const p = new URLSearchParams(params); Object.entries(patch).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k))); setParams(p, { replace: true }); };
  const active = CO_FILTER_KEYS.filter((k) => get(k) && k !== "sort");
  const regions = ["la", "norcal", "sd"].filter((r) => list.some((co) => co.region === r) || r === get("region"));
  const salesOpts = [...new Set(list.map((co) => co.salesperson).filter((v) => v && v !== "None"))].sort();
  const groupCount = (gx) => list.filter((co) => gx.statuses.includes(co.status)).length;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="co-quick-filters" data-active={active.length}>
      <div className="relative">
        <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400 pointer-events-none" />
        <input data-testid="co-filter-q" value={get("q")} onChange={(e) => set({ q: e.target.value })} placeholder={zh ? "搜索 CO# / 地址 / 客户 / 范围 / 销售" : "Search CO# / address / client / scope / sales"} className={`${sel} pl-8 w-64 h-9`} />
      </div>
      <select data-testid="co-filter-group" className={`${sel} ${get("group") ? on : ""}`} value={get("group")} onChange={(e) => set({ group: e.target.value })}>
        <option value="">{zh ? "全部阶段" : "All stages"}</option>
        {CO_GROUPS.map((gx) => <option key={gx.id} value={gx.id}>{zh ? gx.zh : gx.en}（{groupCount(gx)}）</option>)}
      </select>
      <ScopeSelect kind="region" data-testid="co-filter-region" className={`${sel} ${get("region") ? on : ""}`} value={get("region")} onChange={(e) => set({ region: e.target.value })}>
        <option value="">{zh ? "全部分公司" : "All branches"}</option>
        {regions.map((r) => <option key={r} value={r}>{(REGION_LABELS[r] || {})[zh ? "zh" : "en"] || r}（{list.filter((co) => co.region === r).length}）</option>)}
      </ScopeSelect>
      <select data-testid="co-filter-lock" className={`${sel} ${get("lock") ? on : ""}`} value={get("lock")} onChange={(e) => set({ lock: e.target.value })}>
        <option value="">{zh ? "施工锁：全部" : "Lock: all"}</option>
        <option value="locked">{zh ? "锁定中（未放行）" : "Locked"}（{list.filter((co) => !isUnlocked(co)).length}）</option>
        <option value="unlocked">{zh ? "已解锁（可施工）" : "Unlocked"}（{list.filter(isUnlocked).length}）</option>
      </select>
      {salesOpts.length > 0 && (
        <select data-testid="co-filter-sales" className={`${sel} ${get("sales") ? on : ""}`} value={get("sales")} onChange={(e) => set({ sales: e.target.value })}>
          <option value="">{zh ? "全部销售" : "All sales"}</option>
          {salesOpts.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      )}
      <CreatedFilter zh={zh} testId="co-filter-created" className={sel} count={count} unit={zh ? "单" : "COs"} title={zh ? "按变更单创建时间筛选" : "Filter by created date"}
        value={{ mode: get("created"), from: get("cfrom"), to: get("cto") }} onChange={(v) => set({ created: v.mode, cfrom: v.mode === "custom" ? v.from : "", cto: v.mode === "custom" ? v.to : "" })} />
      <span className="inline-flex items-center"><ArrowUpDown className="h-3.5 w-3.5 text-slate-400 -mr-6 ml-2 relative z-10 pointer-events-none" /><select data-testid="co-filter-sort" className={`${sel} pl-7 ${get("sort") ? on : ""}`} value={get("sort")} onChange={(e) => set({ sort: e.target.value })}>
        {SORTS.map((o) => <option key={o.id} value={o.id}>{zh ? o.zh : o.en}</option>)}
      </select></span>
      <span className="text-xs text-slate-500 tabular-nums" data-testid="co-filter-count" data-count={count}>{zh ? `${count} / ${list.length} 单` : `${count} / ${list.length}`}</span>
      {active.length > 0 && (
        <button type="button" data-testid="co-filter-clear" onClick={() => set(Object.fromEntries(CO_FILTER_KEYS.map((k) => [k, ""])))} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-red-600 px-2 py-1.5 rounded-lg hover:bg-red-50">
          <X className="h-3.5 w-3.5" />{zh ? `清除筛选（${active.length}）` : `Clear (${active.length})`}
        </button>
      )}
    </div>
  );
};
